/**
 * Pure preview arithmetic for a comment thread: which comment a collapsed pin
 * shows when it expands, how many replies that hides, and the segments the
 * card renders. Over plain data, so the hover card and the dragged card can
 * share one answer without a Liveblocks room.
 */

/** The subset of a Liveblocks `CommentBody` the preview walks. */
export type PreviewCommentBody = {
  content?: {
    children?: { text?: string; type?: string; url?: string; id?: string }[];
  }[];
};

export type PreviewComment = {
  userId: string;
  createdAt: Date;
  deletedAt?: Date;
  body?: PreviewCommentBody;
};

/**
 * A mention keeps its id, not a name: resolving one is a hook, and this module
 * stays pure. The card turns the id into `@<display name>`.
 */
export type PreviewSegment =
  | { type: "text"; text: string }
  | { type: "mention"; userId: string };

export type CommentPreview = {
  userId: string;
  createdAt: Date;
  segments: PreviewSegment[];
  /** Live comments after the first — tombstones excluded. */
  replyCount: number;
};

/** Roughly three clamped lines at the card's width. */
export const PREVIEW_MAX_CHARS = 140;

/**
 * What a mention spends of that budget. Its rendered width is a display name
 * this module cannot resolve, so it stands in for one.
 */
export const MENTION_PREVIEW_CHARS = 12;

/**
 * The thread as a pin wears it: its opening comment, and the count of what
 * came after. A thread whose every comment is deleted has no preview — the
 * pin falls back to its bare silhouette.
 */
export function selectCommentPreview(
  comments: PreviewComment[],
): CommentPreview | null {
  const live = comments.filter((comment) => !comment.deletedAt);
  const first = live[0];
  if (!first) return null;

  return {
    userId: first.userId,
    createdAt: first.createdAt,
    segments: truncatePreviewSegments(commentBodyToSegments(first.body)),
    replyCount: live.length - 1,
  };
}

/** Body rich text, flattened to one line's worth of segments. */
export function commentBodyToSegments(
  body: PreviewCommentBody | undefined,
): PreviewSegment[] {
  if (!body?.content) return [];

  const segments: PreviewSegment[] = [];
  body.content.forEach((block, index) => {
    if (index > 0) segments.push({ type: "text", text: " " });
    for (const inline of block.children ?? []) {
      if (inline.type === "mention") {
        segments.push({ type: "mention", userId: inline.id ?? "" });
      } else if (inline.type === "link") {
        segments.push({
          type: "text",
          text: (inline.text ?? inline.url) || "",
        });
      } else {
        segments.push({ type: "text", text: inline.text ?? "" });
      }
    }
  });

  return normalizeSegments(segments);
}

/** Merges adjacent text, collapses whitespace, trims the two ends. */
function normalizeSegments(segments: PreviewSegment[]): PreviewSegment[] {
  const merged: PreviewSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (segment.type === "text" && last?.type === "text") {
      merged[merged.length - 1] = {
        type: "text",
        text: last.text + segment.text,
      };
    } else {
      merged.push(segment);
    }
  }

  const collapsed = merged.map((segment) =>
    segment.type === "text"
      ? { type: "text" as const, text: segment.text.replace(/\s+/g, " ") }
      : segment,
  );

  const first = collapsed[0];
  if (first?.type === "text") {
    collapsed[0] = { type: "text", text: first.text.trimStart() };
  }
  const last = collapsed[collapsed.length - 1];
  if (last?.type === "text") {
    collapsed[collapsed.length - 1] = {
      type: "text",
      text: last.text.trimEnd(),
    };
  }

  return collapsed.filter(
    (segment) => segment.type !== "text" || segment.text !== "",
  );
}

/**
 * Cuts the run of segments to the character budget, mentions counted at
 * `MENTION_PREVIEW_CHARS`. A mention is kept whole or dropped; text cuts on a
 * word boundary when one is near the limit, on the character otherwise.
 */
export function truncatePreviewSegments(
  segments: PreviewSegment[],
  max: number = PREVIEW_MAX_CHARS,
): PreviewSegment[] {
  const kept: PreviewSegment[] = [];
  let budget = max;

  for (const segment of segments) {
    if (segment.type === "mention") {
      if (budget < MENTION_PREVIEW_CHARS) return withEllipsis(kept);
      budget -= MENTION_PREVIEW_CHARS;
      kept.push(segment);
      continue;
    }

    if (segment.text.length <= budget) {
      budget -= segment.text.length;
      kept.push(segment);
      continue;
    }

    kept.push({
      type: "text",
      text: truncatePreviewText(segment.text, budget),
    });
    return kept;
  }

  return kept;
}

/** Cuts on a word boundary when one is near the limit, on the character otherwise. */
export function truncatePreviewText(
  text: string,
  max: number = PREVIEW_MAX_CHARS,
): string {
  if (text.length <= max) return text;

  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(" ");
  const kept = space > max * 0.6 ? cut.slice(0, space) : cut;
  return `${kept.trimEnd()}…`;
}

/** Marks a run cut short between segments rather than inside one. */
function withEllipsis(segments: PreviewSegment[]): PreviewSegment[] {
  const last = segments[segments.length - 1];
  if (last?.type === "text") {
    return [
      ...segments.slice(0, -1),
      { type: "text", text: `${last.text.trimEnd()}…` },
    ];
  }
  return [...segments, { type: "text", text: "…" }];
}
